/**
 * Schwarzmarkt-Tab – Crime Ops (/business/crime-ops, Tab SCHWARZMARKT, NT-SCHWARZMARKT-REWORK-01).
 *
 * Kontakt-Angebote statt Einzel-Angebot: Suche starten (wertbasierte Dauer) → 2–3 benannte
 * Kontakte → Mehrfachauswahl + optionale Verhandlung mit genau einem Kontakt → Finalisierung.
 * Kein Auto-Resolve mehr (das war der ursprüngliche Bug-Report) — der Spieler behält jederzeit
 * sein Recht, ein Angebot vollständig abzulehnen.
 *
 * Preis-Breakdown-Zeilen kommen 1:1 vom Backend (BlackMarketContactView.priceBreakdown) — das
 * Frontend rechnet die Pipeline selbst nirgends nach, nur die Live-Auswahl-Summe (Mengen-
 * Allokation + bereits von der API gelieferte Stückpreise) wird hier aggregiert.
 */

import { api, ApiError } from '../../services/api.js';
import { showToast } from '../../utils/toast.js';
import { refreshBalance } from '../../utils/refreshBalance.js';
import { getCleanItemName, getQualityBadge, getLaborOutputBadge } from '../../utils/item-badges.js';

// ─── Typen (spiegeln Backend-Views) ────────────────────────────────────────────

type OfferStatus = 'SEARCHING' | 'OFFERS_READY' | 'RESOLVED';
type ContactHook =
  | 'NONE' | 'WANTED_SMALL' | 'WANTED_LARGE' | 'NO_NEGOTIATION' | 'NEGOTIATION_EASY'
  | 'NEGOTIATION_HARD' | 'PAY_LATER' | 'DOWN_PAYMENT' | 'ROUNDS_DOWN' | 'WANTS_EXTRA'
  | 'HUSH_MONEY' | 'KNOWS_SOMEBODY' | 'PREPAY_RISK';
type ContactStatus = 'ACTIVE' | 'NEGOTIATION_FAILED' | 'SOLD';

interface PriceBreakdownStep {
  label:       string;
  factor:      number | null;
  resultCents: string;
}

interface BlackMarketContactView {
  id:               string;
  name:             string;
  hook:             ContactHook;
  status:           ContactStatus;
  capacityQuantity: number;
  takeQuantity:     number;
  priceBreakdown:   PriceBreakdownStep[];
  unitPriceCents:   string;
  totalPriceCents:  string;
  negotiable:       boolean;
}

interface BlackMarketOfferView {
  id:                         string;
  itemKey:                    string;
  itemType:                   string;
  totalQuantity:              number;
  remainingQuantity:          number;
  status:                     OfferStatus;
  createdAt:                  string;
  searchDoneAt:               string;
  remainingSearchMs:          number;
  negotiationUsed:            boolean;
  contacts:                   BlackMarketContactView[];
  dealerNetPriceCentsPerUnit: string | null;
  /** NT-ORG-07b — Verkauf aus dem Org-Lager: Erlös geht in die Org-Kasse. */
  fuerOrg:                    boolean;
}

interface BlackMarktStatusResponse {
  activeOffers: BlackMarketOfferView[];
}

interface SellableItem {
  propertyId:       string;
  propertyLabel:    string;
  itemKey:          string;
  itemType:         string;
  quantity:         number;
  streetPriceCents: string;
}

interface FinalizeResult {
  offer:             BlackMarketOfferView;
  totalPaidCents:    string;
  wantedPointsAdded: number;
}

// ─── Named Constants (Anzeige-Duplikate der Backend-Konstanten, kein Shared-Import für reine
// Anzeigewerte — etabliertes Pattern, siehe CLAUDE.md) ─────────────────────────────────────

const SM_SEARCH_BASE_MINUTES      = 4;
const SM_SEARCH_MINUTES_PER_STEP  = 2;
// Chris-Feedback 2026-08-01: von 10.000 auf 1.000 S-₦/Schritt gesenkt — vorher änderte sich die
// Vorschau bei typischen Lagermengen/Preisen praktisch nie sichtbar (siehe schwarzmarkt.constants.ts).
const SM_SEARCH_VALUE_STEP_CENTS  = 100_000; // 1.000 S-₦
const SM_SEARCH_MAX_MINUTES       = 20;
const SM_EXTRA_CONTACT_WANTED_POINTS = 8;
const SM_HOOK_WANTED_POINTS_SMALL    = 5;
const SM_HOOK_WANTED_POINTS_LARGE    = 15;
const SM_HOOK_EXTRA_UNITS            = 3;
// NT-POLISH-01 (P6): Schweigegeld prozentual — Spiegel von schwarzmarkt.constants.ts
// (calcSchweigegeldCents), reine Anzeige, Server bleibt die Wahrheit.
const SM_SCHWEIGEGELD_PROZENT        = 5;
const SM_SCHWEIGEGELD_MIN            = 50; // S-₦

function calcSchweigegeldCents(grossCents: bigint): bigint {
  const percentCents = (grossCents * BigInt(SM_SCHWEIGEGELD_PROZENT)) / 100n;
  const wholeUnits   = (percentCents / 100n) * 100n;
  const minCents     = BigInt(SM_SCHWEIGEGELD_MIN) * 100n;
  return wholeUnits > minCents ? wholeUnits : minCents;
}
const SM_HOOK_PAY_LATER_MINUTES      = 30;
const SM_HOOK_KNOWS_SOMEBODY_BONUS   = 0.10;
const SM_PREPAY_RISK_FAIL_CHANCE     = 0.08;
const SM_NEGOTIATION_CHANCE_NORMAL   = 0.20;
const SM_NEGOTIATION_CHANCE_EASY     = 0.35;
const SM_NEGOTIATION_CHANCE_HARD     = 0.10;

const HOOK_LABELS: Record<ContactHook, string> = {
  NONE:             '',
  WANTED_SMALL:     '+FAHNDUNG',
  WANTED_LARGE:     '+FAHNDUNG (HOCH)',
  NO_NEGOTIATION:   'VERHANDELT NICHT',
  NEGOTIATION_EASY: 'VERHANDLUNG LEICHT',
  NEGOTIATION_HARD: 'VERHANDLUNG SCHWER',
  PAY_LATER:        'ZAHLT SPÄTER',
  DOWN_PAYMENT:     'ANZAHLUNG',
  ROUNDS_DOWN:      'RUNDET AB',
  WANTS_EXTRA:      'WILL ZUGABE',
  HUSH_MONEY:       'SCHWEIGEGELD',
  KNOWS_SOMEBODY:   'KENNT JEMANDEN',
  PREPAY_RISK:      'VORKASSE-RISIKO',
};

/**
 * Trait-Legende (NT-UI-SCHWARZMARKT-01, #B) — Texte sind 1:1 aus den echten Effekten in
 * schwarzmarkt.service.ts/schwarzmarkt.constants.ts abgeleitet (finalizeOffer/negotiateContact),
 * nicht erfunden. Gepflegt an dieser einen Stelle, genutzt von Legende UND Badge-title.
 */
interface TraitInfo { label: string; effekt: string; }

const SCHWARZMARKT_TRAIT_INFO: Record<Exclude<ContactHook, 'NONE'>, TraitInfo> = {
  WANTED_SMALL:     { label: HOOK_LABELS.WANTED_SMALL,     effekt: `Verkauf an diesen Kontakt bringt +${String(SM_HOOK_WANTED_POINTS_SMALL)} Fahndungspunkte.` },
  WANTED_LARGE:     { label: HOOK_LABELS.WANTED_LARGE,     effekt: `Verkauf an diesen Kontakt bringt +${String(SM_HOOK_WANTED_POINTS_LARGE)} Fahndungspunkte.` },
  NO_NEGOTIATION:   { label: HOOK_LABELS.NO_NEGOTIATION,   effekt: 'Mit diesem Kontakt kann nicht verhandelt werden.' },
  NEGOTIATION_EASY: { label: HOOK_LABELS.NEGOTIATION_EASY, effekt: `Verhandlungs-Erfolgschance ${String(Math.round(SM_NEGOTIATION_CHANCE_EASY * 100))}% (statt normal ${String(Math.round(SM_NEGOTIATION_CHANCE_NORMAL * 100))}%).` },
  NEGOTIATION_HARD: { label: HOOK_LABELS.NEGOTIATION_HARD, effekt: `Verhandlungs-Erfolgschance nur ${String(Math.round(SM_NEGOTIATION_CHANCE_HARD * 100))}% (statt normal ${String(Math.round(SM_NEGOTIATION_CHANCE_NORMAL * 100))}%).` },
  PAY_LATER:        { label: HOOK_LABELS.PAY_LATER,        effekt: `Auszahlung erfolgt erst ${String(SM_HOOK_PAY_LATER_MINUTES)} Min. nach dem Verkauf, nicht sofort.` },
  DOWN_PAYMENT:     { label: HOOK_LABELS.DOWN_PAYMENT,     effekt: `Nur die Hälfte wird sofort gezahlt, der Rest erst ${String(SM_HOOK_PAY_LATER_MINUTES)} Min. später.` },
  ROUNDS_DOWN:      { label: HOOK_LABELS.ROUNDS_DOWN,      effekt: 'Die abgenommene Menge wird auf volle 10er abgerundet.' },
  WANTS_EXTRA:      { label: HOOK_LABELS.WANTS_EXTRA,      effekt: `Nimmt zusätzlich bis zu ${String(SM_HOOK_EXTRA_UNITS)} Stück gratis aus dem Rest mit.` },
  HUSH_MONEY:       { label: HOOK_LABELS.HUSH_MONEY,       effekt: `Zieht ${String(SM_SCHWEIGEGELD_PROZENT)}% vom Erlös dieses Kontakts ab, mindestens ${String(SM_SCHWEIGEGELD_MIN)} S-₦.` },
  KNOWS_SOMEBODY:   { label: HOOK_LABELS.KNOWS_SOMEBODY,   effekt: `Verbessert die Preisspanne der nächsten Suche um +${String(Math.round(SM_HOOK_KNOWS_SOMEBODY_BONUS * 100))}%.` },
  PREPAY_RISK:      { label: HOOK_LABELS.PREPAY_RISK,      effekt: `${String(Math.round(SM_PREPAY_RISK_FAIL_CHANCE * 100))}% Chance, dass die Auszahlung komplett ausfällt.` },
};

const SM_HEHLER_SATZ_INFO: TraitInfo = {
  label:  'HEHLER-SATZ',
  effekt: 'Anteil des angepassten Straßenpreises, den dieser Kontakt zahlt — z. B. ×0,60 = 60%.',
};

/** Nachfrage-Spitze (Backend: SCHWARZMARKT_GROSSABNEHMER_*). Reine Anzeige, kein Shared-Import. */
const SM_GROSSABNEHMER_INFO: TraitInfo = {
  label:  'GROSSABNEHMER',
  effekt: 'Taucht nur selten auf. Zahlt 1,2- bis 1,8-fach den Straßenpreis, nimmt aber nur einen kleinen Teil der Menge und verhandelt nicht.',
};

function estimateSearchMinutes(grossValueCents: number): number {
  const steps = Math.floor(grossValueCents / SM_SEARCH_VALUE_STEP_CENTS);
  return Math.min(SM_SEARCH_MAX_MINUTES, SM_SEARCH_BASE_MINUTES + SM_SEARCH_MINUTES_PER_STEP * steps);
}

// ─── State ────────────────────────────────────────────────────────────────────

let isMounted = false;
let statusData: BlackMarktStatusResponse | null = null;
let sellableItems: SellableItem[] = [];
let selectedSellValue = '';
let sellQty = 1;
// offerId -> ausgewählte contactIds in Anhak-Reihenfolge
let selectedContactsByOffer: Record<string, string[]> = {};
let legendExpanded = false;
let reloadPromise: Promise<void> | null = null;
let countdownTimer: ReturnType<typeof setInterval> | null = null;
let clickHandler: ((e: Event) => void) | null = null;
let changeHandler: ((e: Event) => void) | null = null;

// ─── Styles ───────────────────────────────────────────────────────────────────

function addSchwarzmarktStyles(): void {
  const id = 'schwarzmarkt-tab-styles';
  let el = document.getElementById(id) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = `
    #sm-root { font-family: var(--font-mono); }
    .sm-section-title { font-size: var(--font-size-base); text-transform: uppercase; letter-spacing: 0.08em; margin: var(--space-4) 0 var(--space-2) 0; color: var(--text-primary); }
    .sm-empty { color: var(--text-muted); font-size: var(--font-size-sm); padding: var(--space-3) 0; }
    .sm-offer-card { border: 1px solid var(--border); border-radius: var(--radius); padding: var(--space-3); margin-bottom: var(--space-3); background: var(--bg-surface); }
    .sm-offer-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: var(--space-2); }
    .sm-offer-title { font-size: var(--font-size-sm); color: var(--text-primary); text-transform: uppercase; }
    .sm-offer-org { font-size: var(--font-size-xs); color: var(--accent-purple); border: 1px solid var(--accent-purple); border-radius: var(--radius-sm); padding: 0 var(--space-1); margin-left: var(--space-2); }
    .sm-searching-meta { font-size: var(--font-size-xs); color: var(--text-muted); }
    .sm-dealer-line { font-size: var(--font-size-xs); color: var(--text-muted); margin-bottom: var(--space-2); }
    /* Kontakt-Karte (#B): Checkbox in fester Grid-Spalte oben-links, Body rechts daneben —
       "nimmt X von Y" ist eine eigene Block-Zeile im Body (kein Flex-Nebeneinander mit dem
       Namen), dadurch auf jeder Breite überlappungsfrei, nicht nur ab einem Mobile-Breakpoint. */
    .sm-contact-card { display: grid; grid-template-columns: 18px 1fr; column-gap: var(--space-2); align-items: start; border: 1px solid var(--border); border-radius: var(--radius); padding: var(--space-2); margin-bottom: var(--space-2); background: var(--bg-elevated); }
    .sm-contact-checkbox { grid-column: 1; margin: 3px 0 0 0; padding: 0; border: none; background: transparent; appearance: checkbox; -webkit-appearance: checkbox; width: 16px; height: 16px; cursor: pointer; accent-color: var(--accent-purple); }
    .sm-contact-checkbox:disabled { cursor: default; opacity: 0.4; }
    .sm-contact-body { grid-column: 2; min-width: 0; }
    .sm-contact-head { display: flex; align-items: center; gap: var(--space-2); flex-wrap: wrap; margin-bottom: 4px; }
    .sm-contact-name { font-size: var(--font-size-sm); color: var(--text-primary); text-transform: uppercase; }
    .sm-contact-take { font-size: var(--font-size-sm); color: var(--accent-gold); font-weight: bold; margin-bottom: var(--space-2); }
    .sm-contact-take strong { color: var(--accent-gold); }
    .sm-hook-badge { font-size: 10px; padding: 2px 6px; border-radius: var(--radius-sm); text-transform: uppercase; background: color-mix(in srgb, var(--accent-orange) 18%, transparent); color: var(--accent-orange); border: 1px solid color-mix(in srgb, var(--accent-orange) 45%, transparent); cursor: help; }
    /* ── Trait-Legende (#B) ── */
    .sm-legend { margin-bottom: var(--space-3); }
    .sm-legend-toggle { font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--accent-blue); background: transparent; border: 1px solid var(--accent-blue); border-radius: var(--radius-btn); padding: 4px 10px; cursor: pointer; text-transform: uppercase; letter-spacing: 0.04em; }
    .sm-legend-toggle:hover { background: color-mix(in srgb, var(--accent-blue) 12%, transparent); }
    .sm-legend-panel { border: 1px solid var(--border); border-radius: var(--radius); padding: var(--space-2) var(--space-3); margin-top: var(--space-2); background: var(--bg-surface); }
    .sm-legend-row { display: flex; gap: var(--space-3); padding: 4px 0; border-bottom: 1px solid var(--border); font-size: var(--font-size-xs); }
    .sm-legend-row:last-child { border-bottom: none; }
    .sm-legend-label { flex: 0 0 160px; color: var(--accent-orange); text-transform: uppercase; }
    .sm-legend-text { flex: 1; color: var(--text-muted); }
    @media (max-width: 480px) {
      .sm-legend-row { flex-direction: column; gap: 2px; }
      .sm-legend-label { flex: none; }
    }
    .sm-breakdown { width: 100%; border-collapse: collapse; margin: var(--space-2) 0; font-size: var(--font-size-xs); }
    .sm-breakdown td { padding: 2px 0; color: var(--text-muted); }
    .sm-breakdown td:last-child { text-align: right; color: var(--text-primary); }
    .sm-breakdown tr:last-child td { border-top: 1px solid var(--border); padding-top: var(--space-1); font-weight: bold; color: var(--accent-green); }
    .sm-contact-total { font-size: var(--font-size-xs); color: var(--text-muted); margin-bottom: var(--space-2); }
    .sm-negotiate-btn { font-size: var(--font-size-xs); }
    .sm-summary-bar { border: 1px solid var(--accent-gold); border-radius: var(--radius); padding: var(--space-3); margin: var(--space-3) 0; background: color-mix(in srgb, var(--accent-gold) 8%, transparent); }
    .sm-summary-row { display: flex; justify-content: space-between; font-size: var(--font-size-sm); flex-wrap: wrap; gap: var(--space-2); }
    .sm-summary-label { color: var(--text-muted); text-transform: uppercase; font-size: var(--font-size-xs); }
    .sm-summary-value { color: var(--text-primary); }
    .sm-summary-rest { font-size: var(--font-size-xs); color: var(--text-muted); margin-top: var(--space-1); }
    .sm-form-hint { font-size: var(--font-size-xs); color: var(--text-muted); margin-top: var(--space-2); line-height: 1.5; }
    .sm-duration-preview { font-size: var(--font-size-xs); color: var(--accent-gold); margin: var(--space-2) 0; }
    .sm-err { color: var(--accent-red); font-size: var(--font-size-xs); margin-top: var(--space-2); display: none; }
    /* Mengen-Steuerung (+/- + Eingabefeld) — lokal definiert statt von markt.ts's laufzeit-
       injizierten Styles abhängig zu sein (sonst ungestyled, falls der Markt-Tab in dieser
       Session noch nie besucht wurde). */
    .markt-qty-btn { font-family: var(--font-mono); font-size: var(--font-size-sm); height: 36px; min-width: 36px; border: 1px solid var(--border); background: var(--bg-surface); color: var(--text-primary); border-radius: var(--radius-btn); cursor: pointer; padding: 0 var(--space-1); touch-action: manipulation; }
    .markt-qty-btn:hover { border-color: var(--text-muted); }
    .markt-qty-input { font-family: var(--font-mono); font-size: var(--font-size-sm); height: 36px; width: 64px; text-align: center; background: var(--bg-surface); border: 1px solid var(--border); color: var(--accent-gold); border-radius: var(--radius-btn); padding: 0 var(--space-1); -moz-appearance: textfield; }
    .markt-qty-input::-webkit-outer-spin-button, .markt-qty-input::-webkit-inner-spin-button { -webkit-appearance: none; }
  `;
}

// ─── Hilfsfunktionen ────────────────────────────────────────────────────────

function esc(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function fmtMoney(cents: string | number): string {
  const n = (typeof cents === 'string' ? parseInt(cents, 10) : cents) / 100;
  return `${n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} S-₦`;
}

function fmtFactor(factor: number): string {
  return `×${factor.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtCountdown(ms: number): string {
  if (ms <= 0) return 'GLEICH';
  const totalSeconds = Math.floor(ms / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m)}m ${String(s).padStart(2, '0')}s`;
}

/** Anzeigename inkl. Qualitäts-/Reinheits-Label (item-badges.ts ist die einzige Badge-Quelle). */
function itemDisplayLabel(itemKey: string): string {
  const badge = getQualityBadge(itemKey) ?? getLaborOutputBadge(itemKey);
  const name = getCleanItemName(itemKey).toUpperCase();
  return badge ? `${name} (${badge.label})` : name;
}

// ─── Live-Auswahl-Summe (Mengen-Allokation, keine Preis-Pipeline-Neuberechnung) ────────────

interface SelectionSummary {
  soldQty:      number;
  restQty:      number;
  earnedCents:  bigint;
  wantedPoints: number;
}

function computeSelectionSummary(offer: BlackMarketOfferView, selectedIds: string[]): SelectionSummary {
  let remaining = offer.remainingQuantity;
  let earnedCents = 0n;
  let wantedPoints = 0;
  let soldContactIdx = 0;

  for (const id of selectedIds) {
    if (remaining <= 0) break;
    const contact = offer.contacts.find((c) => c.id === id);
    if (!contact) continue;

    let take = Math.min(contact.capacityQuantity, remaining);
    // RUNDET AB rundet nur, wenn danach noch etwas übrig bleibt — sonst nimmt der Kontakt die
    // komplette (kleinere) Restmenge. Muss exakt mit applyRoundsDownHook() (schwarzmarkt.service.ts)
    // übereinstimmen, sonst weicht diese Live-Summe von der tatsächlichen Auszahlung ab.
    if (contact.hook === 'ROUNDS_DOWN' && take >= 10) take = Math.floor(take / 10) * 10;
    if (take <= 0) continue;

    let contactCents = BigInt(contact.unitPriceCents) * BigInt(take);
    if (contact.hook === 'HUSH_MONEY') {
      contactCents -= calcSchweigegeldCents(contactCents);
      if (contactCents < 0n) contactCents = 0n;
    }
    if (contact.hook === 'PAY_LATER') contactCents = 0n; // erst nach Verzögerung, nicht im "sofort"-Erlös
    if (contact.hook === 'DOWN_PAYMENT') contactCents /= 2n;
    earnedCents += contactCents;

    if (soldContactIdx > 0) wantedPoints += SM_EXTRA_CONTACT_WANTED_POINTS;
    if (contact.hook === 'WANTED_SMALL') wantedPoints += SM_HOOK_WANTED_POINTS_SMALL;
    if (contact.hook === 'WANTED_LARGE') wantedPoints += SM_HOOK_WANTED_POINTS_LARGE;
    soldContactIdx++;

    let extraTaken = 0;
    if (contact.hook === 'WANTS_EXTRA') extraTaken = Math.min(SM_HOOK_EXTRA_UNITS, remaining - take);
    remaining -= take + extraTaken;
  }

  return { soldQty: offer.remainingQuantity - remaining, restQty: remaining, earnedCents, wantedPoints };
}

// ─── Render ───────────────────────────────────────────────────────────────────

function renderBreakdownTable(breakdown: PriceBreakdownStep[]): string {
  const rows = breakdown.map((step) => `
    <tr>
      <td>${esc(step.label)}</td>
      <td>${step.factor !== null ? fmtFactor(step.factor) : ''}</td>
      <td>${fmtMoney(step.resultCents)}</td>
    </tr>
  `).join('');
  return `<table class="sm-breakdown"><tbody>${rows}</tbody></table>`;
}

function renderContactCard(offer: BlackMarketOfferView, contact: BlackMarketContactView): string {
  const checkboxKey = `${offer.id}::${contact.id}`;
  const isSelected = (selectedContactsByOffer[offer.id] ?? []).includes(contact.id);
  const hookInfo = contact.hook !== 'NONE' ? SCHWARZMARKT_TRAIT_INFO[contact.hook] : null;
  const hookBadge = hookInfo
    ? `<span class="sm-hook-badge" title="${esc(`${hookInfo.label}: ${hookInfo.effekt}`)}">${esc(HOOK_LABELS[contact.hook])}</span>`
    : '';
  const negotiateBtn = contact.negotiable
    ? `<button class="co-btn sm-negotiate-btn" data-sm-negotiate="${esc(offer.id)}::${esc(contact.id)}">VERHANDELN</button>`
    : '';

  return `
    <div class="sm-contact-card">
      <input type="checkbox" class="sm-contact-checkbox" data-sm-select="${esc(checkboxKey)}" ${isSelected ? 'checked' : ''} ${contact.takeQuantity <= 0 ? 'disabled' : ''}>
      <div class="sm-contact-body">
        <div class="sm-contact-head">
          <span class="sm-contact-name">${esc(contact.name)}</span>
          ${hookBadge}
        </div>
        <div class="sm-contact-take">nimmt <strong>${String(contact.takeQuantity)}</strong> von ${String(offer.remainingQuantity)}</div>
        ${renderBreakdownTable(contact.priceBreakdown)}
        <div class="sm-contact-total">${fmtMoney(contact.unitPriceCents)}/Stk · ${fmtMoney(contact.totalPriceCents)} gesamt</div>
        ${contact.hook === 'HUSH_MONEY' ? `<div class="sm-contact-take">SCHWEIGEGELD: −${fmtMoney(String(calcSchweigegeldCents(BigInt(contact.unitPriceCents) * BigInt(Math.max(0, contact.takeQuantity)))))}</div>` : ''}
        ${negotiateBtn}
      </div>
    </div>
  `;
}

/** Trait-Legende (#B, "? WAS BEDEUTET DAS?") — alle Effekte aus SCHWARZMARKT_TRAIT_INFO + Hehler-Satz. */
function renderTraitLegend(): string {
  const hookRows = (Object.keys(SCHWARZMARKT_TRAIT_INFO) as (keyof typeof SCHWARZMARKT_TRAIT_INFO)[])
    .map((hook) => {
      const info = SCHWARZMARKT_TRAIT_INFO[hook];
      return `<div class="sm-legend-row"><span class="sm-legend-label">${esc(info.label)}</span><span class="sm-legend-text">${esc(info.effekt)}</span></div>`;
    }).join('');
  const hehlerRow = [SM_HEHLER_SATZ_INFO, SM_GROSSABNEHMER_INFO]
    .map((info) => `<div class="sm-legend-row"><span class="sm-legend-label">${esc(info.label)}</span><span class="sm-legend-text">${esc(info.effekt)}</span></div>`)
    .join('');

  return `
    <div class="sm-legend">
      <button type="button" class="sm-legend-toggle" data-sm-toggle-legend>${legendExpanded ? '[×]' : '[?]'} WAS BEDEUTET DAS?</button>
      ${legendExpanded ? `<div class="sm-legend-panel">${hookRows}${hehlerRow}</div>` : ''}
    </div>
  `;
}

function renderOfferCard(offer: BlackMarketOfferView): string {
  const itemLabel = itemDisplayLabel(offer.itemKey);

  if (offer.status === 'SEARCHING') {
    return `
      <div class="sm-offer-card">
        <div class="sm-offer-header">
          <span class="sm-offer-title">${esc(itemLabel)} × ${String(offer.totalQuantity)}${offer.fuerOrg ? ' <span class="sm-offer-org">FÜR DIE ORG</span>' : ''}</span>
          <span class="co-status-badge kredit">SUCHE</span>
        </div>
        <div class="sm-searching-meta">Abnehmer-Suche läuft... <span data-sm-countdown="${esc(offer.id)}">${fmtCountdown(offer.remainingSearchMs)}</span></div>
      </div>
    `;
  }

  const selected = selectedContactsByOffer[offer.id] ?? [];
  const summary = computeSelectionSummary(offer, selected);
  const dealerLine = offer.dealerNetPriceCentsPerUnit !== null
    ? `<div class="sm-dealer-line">Dealer zahlt ~${fmtMoney(offer.dealerNetPriceCentsPerUnit)}/Stk — braucht aber Zeit.</div>`
    : '';

  const contactsHtml = offer.contacts.length === 0
    ? '<p class="sm-empty">Keine Kontakte gemeldet.</p>'
    : offer.contacts.map((c) => renderContactCard(offer, c)).join('');

  const sellBtnLabel = summary.soldQty > 0 ? 'VERKAUFEN' : 'ABLEHNEN';

  return `
    <div class="sm-offer-card">
      <div class="sm-offer-header">
        <span class="sm-offer-title">${esc(itemLabel)} × ${String(offer.remainingQuantity)}${offer.fuerOrg ? ' <span class="sm-offer-org">FÜR DIE ORG</span>' : ''}</span>
        <span class="co-status-badge aktiv">${String(offer.contacts.length)} KONTAKTE</span>
      </div>
      ${dealerLine}
      ${contactsHtml}
      <div class="sm-summary-bar">
        <div class="sm-summary-row">
          <div><span class="sm-summary-label">Ausgewählt</span><br><span class="sm-summary-value">${String(selected.length)} Kontakte</span></div>
          <div><span class="sm-summary-label">Verkauft</span><br><span class="sm-summary-value">${String(summary.soldQty)} von ${String(offer.remainingQuantity)}</span></div>
          <div><span class="sm-summary-label">Erlös</span><br><span class="sm-summary-value" style="color:var(--accent-green);">${fmtMoney(summary.earnedCents.toString())}</span></div>
          <div><span class="sm-summary-label">Fahndung</span><br><span class="sm-summary-value" style="color:${summary.wantedPoints > 0 ? 'var(--accent-red)' : 'var(--text-muted)'};">+${String(summary.wantedPoints)}</span></div>
        </div>
        ${summary.restQty > 0 ? `<div class="sm-summary-rest">Rest: ${String(summary.restQty)} bleiben im Lager${summary.soldQty > 0 ? ' — für sie ist eine neue Suche nötig' : ''}.</div>` : ''}
      </div>
      <div class="co-actions">
        <button class="co-btn co-btn-primary" data-sm-finalize="${esc(offer.id)}">${sellBtnLabel}</button>
      </div>
      <div class="sm-err" id="sm-err-${esc(offer.id)}"></div>
    </div>
  `;
}

function renderSellForm(): string {
  if (sellableItems.length === 0) {
    return '<p class="sm-empty">Keine verkaufbaren Waren im Lager.</p>';
  }

  const firstItem = sellableItems[0];
  if (!firstItem) return '<p class="sm-empty">Keine verkaufbaren Waren im Lager.</p>';
  const selectedItem = sellableItems.find((i) => `${i.propertyId}::${i.itemKey}` === selectedSellValue) ?? firstItem;
  if (!selectedSellValue) selectedSellValue = `${selectedItem.propertyId}::${selectedItem.itemKey}`;
  const clampedQty = Math.max(1, Math.min(selectedItem.quantity, sellQty));
  if (clampedQty !== sellQty) sellQty = clampedQty;

  const grossValueCents = parseInt(selectedItem.streetPriceCents, 10) * sellQty;
  const minutes = estimateSearchMinutes(grossValueCents);

  return `
    <div class="co-business">
      <p class="co-section-label" style="margin-top:0;">WARE WÄHLEN</p>
      <select class="markt-select" data-sm-item style="margin-bottom:var(--space-3);">
        ${renderSellOptions()}
      </select>
      <p class="co-section-label">MENGE</p>
      <div style="display:flex;align-items:center;gap:var(--space-2);margin-bottom:var(--space-2);">
        <button class="markt-qty-btn" style="color:var(--accent-red);border-color:color-mix(in srgb,var(--accent-red) 45%,transparent);" data-sm-qty="-1">-</button>
        <input type="number" class="markt-qty-input" id="sm-qty-input" min="1" max="${String(selectedItem.quantity)}"
          value="${String(sellQty)}" data-sm-qty-max="${String(selectedItem.quantity)}">
        <button class="markt-qty-btn" style="color:var(--accent-green);border-color:color-mix(in srgb,var(--accent-green) 45%,transparent);" data-sm-qty="+1">+</button>
      </div>
      <div class="sm-duration-preview">Suchdauer: ${String(minutes)} Min. (abhängig vom Chargenwert)</div>
      <div class="co-actions">
        <button class="co-btn co-btn-primary" data-co-action="start-schwarzmarkt">SUCHE STARTEN (${String(minutes)} MIN)</button>
      </div>
      <div class="sm-err" id="sm-start-err"></div>
    </div>
  `;
}

// Anzeige-Reihenfolge der Qualitäts-/Reinheits-Gruppen (beste zuerst), unbekannte Badge-Labels
// (sollte es künftig neue geben) fallen alphabetisch ans Ende, Items ohne Badge unter SONSTIGES.
const QUALITY_OPTGROUP_ORDER: readonly string[] = [
  'PREMIUM-ERNTE', 'FEINWARE', 'MARKTWARE', 'HINTERHOF-WARE', 'AUSSCHUSS',
  'REINWARE', 'PRISMAKRISTALL', 'STRASSENWARE', 'SONSTIGES',
];

function sellItemGroupLabel(itemKey: string): string {
  return getQualityBadge(itemKey)?.label ?? getLaborOutputBadge(itemKey)?.label ?? 'SONSTIGES';
}

/**
 * Natives <select> ohne custom-Dropdown-Pattern im Projekt (Schritt-0 bestätigt, kein anderer
 * Ort im Frontend hat ein solches Pattern) — farbige Badges sind in <option> technisch nicht
 * möglich. Fallback-Variante (S3): Gruppierung via <optgroup> nach Qualitäts-/Reinheits-Badge
 * (item-badges.ts, keine Parallel-Badge-Logik) + Klartext-Segment im Options-Text.
 */
function renderSellOptions(): string {
  const presentGroups = new Set(sellableItems.map((i) => sellItemGroupLabel(i.itemKey)));
  const orderedGroups = [
    ...QUALITY_OPTGROUP_ORDER.filter((g) => presentGroups.has(g)),
    ...[...presentGroups].filter((g) => !QUALITY_OPTGROUP_ORDER.includes(g)).sort(),
  ];

  return orderedGroups.map((groupLabel) => {
    const itemsInGroup = sellableItems.filter((i) => sellItemGroupLabel(i.itemKey) === groupLabel);
    const optionsHtml = itemsInGroup.map((i) => {
      const value = `${i.propertyId}::${i.itemKey}`;
      const name = esc(getCleanItemName(i.itemKey).toUpperCase());
      const badgePart = groupLabel !== 'SONSTIGES' ? ` · ${esc(groupLabel)}` : '';
      return `<option value="${esc(value)}" ${value === selectedSellValue ? 'selected' : ''}>${name}${badgePart} · ${String(i.quantity)} Stk · ${fmtMoney(i.streetPriceCents)}/Stk</option>`;
    }).join('');
    return `<optgroup label="${esc(groupLabel)}">${optionsHtml}</optgroup>`;
  }).join('');
}

function renderContent(): string {
  const offers = statusData?.activeOffers ?? [];
  const hasContactLists = offers.some((o) => o.status === 'OFFERS_READY');
  return `
    ${offers.length > 0 ? `<p class="sm-section-title" style="margin-top:0;">AKTIVE SUCHEN (${String(offers.length)}/3)</p>${hasContactLists ? renderTraitLegend() : ''}${offers.map(renderOfferCard).join('')}` : ''}
    <p class="sm-section-title" style="margin-top:${offers.length > 0 ? 'var(--space-4)' : '0'};">NEUEN VERKAUF STARTEN</p>
    ${renderSellForm()}
  `;
}

export function renderSchwarzmarktTab(): string {
  addSchwarzmarktStyles();
  if (!statusData) return '<div id="sm-root"><p class="sm-empty">LADE...</p></div>';
  return `<div id="sm-root">${renderContent()}</div>`;
}

// ─── Ergebnis-Modal (Finalisierung) ─────────────────────────────────────────────

function showFinalizeResultModal(result: FinalizeResult): void {
  document.getElementById('sm-result-modal')?.remove();

  const earned = fmtMoney(result.totalPaidCents);
  const wanted = result.wantedPointsAdded > 0 ? `<p>Fahndung: +${String(result.wantedPointsAdded)} Punkte.</p>` : '';
  const rest = result.offer.remainingQuantity > 0
    ? `<p>${String(result.offer.remainingQuantity)} Stück blieben im Lager — eine neue Suche ist nötig, um sie erneut anzubieten.</p>`
    : '';

  const el = document.createElement('div');
  el.id = 'sm-result-modal';
  el.className = 'modal-backdrop';
  el.innerHTML = `
    <div class="modal modal--narrow">
      <div class="modal-header"><h2>VERKAUF ABGESCHLOSSEN</h2></div>
      <div class="modal-body">
        <p>Erhalten: <strong style="color:var(--accent-green);">${earned}</strong></p>
        ${wanted}
        ${rest}
      </div>
      <div class="modal-footer"><button class="btn btn--primary" id="sm-result-ok">OK</button></div>
    </div>
  `;
  document.body.appendChild(el);
  document.getElementById('sm-result-ok')?.addEventListener('click', () => { el.remove(); });
}

// ─── Daten laden ──────────────────────────────────────────────────────────────

async function loadAll(): Promise<void> {
  const [status, sellable] = await Promise.all([
    api.get<BlackMarktStatusResponse>('/schwarzmarkt/status'),
    api.get<{ sellableItems: SellableItem[] }>('/schwarzmarkt/sellable-items'),
  ]);
  statusData = status;
  sellableItems = sellable.sellableItems;

  // Auswahl bereinigen: Kontakte/Angebote, die es nicht mehr gibt, aus der Auswahl entfernen
  const validOfferIds = new Set(status.activeOffers.map((o) => o.id));
  const cleanedSelection: Record<string, string[]> = {};
  for (const offerId of Object.keys(selectedContactsByOffer)) {
    if (!validOfferIds.has(offerId)) continue;
    const offer = status.activeOffers.find((o) => o.id === offerId);
    const validContactIds = new Set((offer?.contacts ?? []).map((c) => c.id));
    cleanedSelection[offerId] = (selectedContactsByOffer[offerId] ?? []).filter((id) => validContactIds.has(id));
  }
  selectedContactsByOffer = cleanedSelection;
}

async function reload(): Promise<void> {
  if (reloadPromise) { await reloadPromise; return; }
  reloadPromise = (async () => {
    try {
      await loadAll();
    } finally {
      reloadPromise = null;
    }
    rerender();
  })();
  await reloadPromise;
}

function rerender(): void {
  const root = document.getElementById('sm-root');
  if (!root) return;
  addSchwarzmarktStyles();
  root.innerHTML = statusData ? renderContent() : '<p class="sm-empty">LADE...</p>';
}

function startCountdownTimer(): void {
  if (countdownTimer) { clearInterval(countdownTimer); countdownTimer = null; }
  countdownTimer = setInterval(() => {
    if (!statusData) return;
    let anyDue = false;
    for (const offer of statusData.activeOffers) {
      if (offer.status !== 'SEARCHING') continue;
      const remaining = new Date(offer.searchDoneAt).getTime() - Date.now();
      if (remaining <= 0) { anyDue = true; continue; }
      const el = document.querySelector(`[data-sm-countdown="${offer.id}"]`);
      if (el) el.textContent = fmtCountdown(remaining);
    }
    if (anyDue) void reload();
  }, 1000);
}

// ─── Event-Listener ───────────────────────────────────────────────────────────

function showError(elId: string, message: string): void {
  const el = document.getElementById(elId);
  if (el) { el.textContent = message; el.style.display = 'block'; }
}

function attachListeners(): void {
  const root = document.getElementById('sm-root');
  if (!root) return;

  clickHandler = (e: Event) => {
    const target = e.target as Element;

    const legendToggleBtn = target.closest<HTMLButtonElement>('[data-sm-toggle-legend]');
    if (legendToggleBtn) {
      legendExpanded = !legendExpanded;
      rerender();
      return;
    }

    const qtyBtn = target.closest<HTMLButtonElement>('[data-sm-qty]');
    if (qtyBtn?.dataset.smQty) {
      const delta = parseInt(qtyBtn.dataset.smQty, 10);
      const selectedItem = sellableItems.find((i) => `${i.propertyId}::${i.itemKey}` === selectedSellValue);
      const max = selectedItem?.quantity ?? 1;
      sellQty = Math.max(1, Math.min(max, sellQty + delta));
      rerender();
      return;
    }

    const startBtn = target.closest<HTMLButtonElement>('[data-co-action="start-schwarzmarkt"]');
    if (startBtn) {
      void handleStartSearch();
      return;
    }

    const negotiateBtn = target.closest<HTMLButtonElement>('[data-sm-negotiate]');
    if (negotiateBtn?.dataset.smNegotiate) {
      void handleNegotiate(negotiateBtn.dataset.smNegotiate);
      return;
    }

    const finalizeBtn = target.closest<HTMLButtonElement>('[data-sm-finalize]');
    if (finalizeBtn?.dataset.smFinalize) {
      void handleFinalize(finalizeBtn.dataset.smFinalize);
      return;
    }
  };

  changeHandler = (e: Event) => {
    const target = e.target as HTMLInputElement;

    // Direkte Mengen-Eingabe (statt nur +/-), Muster markt.ts#markt-qty-input — change statt
    // input, damit mehrstellige Zahlen getippt werden können, ohne das Feld pro Tastendruck
    // per Voll-Rerender neu zu mounten (Fokus-/Cursor-Verlust).
    if (target.id === 'sm-qty-input') {
      const selectedItem = sellableItems.find((i) => `${i.propertyId}::${i.itemKey}` === selectedSellValue);
      const maxQty = selectedItem?.quantity ?? parseInt(target.dataset.smQtyMax ?? '1', 10);
      const raw = parseInt(target.value, 10);
      sellQty = Math.max(1, Math.min(maxQty, Number.isFinite(raw) ? raw : 1));
      rerender();
      return;
    }

    if (target.dataset.smItem !== undefined || target.closest('[data-sm-item]')) {
      selectedSellValue = (target as unknown as HTMLSelectElement).value;
      sellQty = 1;
      rerender();
      return;
    }

    if (target.dataset.smSelect !== undefined) {
      const [offerId, contactId] = target.dataset.smSelect.split('::');
      if (!offerId || !contactId) return;
      const current = selectedContactsByOffer[offerId] ?? [];
      selectedContactsByOffer[offerId] = target.checked
        ? [...current, contactId]
        : current.filter((id) => id !== contactId);
      rerender();
      return;
    }
  };

  root.addEventListener('click', clickHandler);
  root.addEventListener('change', changeHandler);
}

function detachListeners(): void {
  const root = document.getElementById('sm-root');
  if (!root || !clickHandler || !changeHandler) return;
  root.removeEventListener('click', clickHandler);
  root.removeEventListener('change', changeHandler);
  clickHandler = null;
  changeHandler = null;
}

// ─── API-Aktionen ─────────────────────────────────────────────────────────────

async function handleStartSearch(): Promise<void> {
  const errEl = document.getElementById('sm-start-err');
  if (errEl) errEl.style.display = 'none';
  const [propertyId, itemKey] = selectedSellValue.split('::');
  if (!propertyId || !itemKey) return;
  try {
    await api.post('/schwarzmarkt/sell', { sourcePropertyId: propertyId, itemKey, quantity: sellQty });
    sellQty = 1;
    await reload();
    startCountdownTimer();
  } catch (err) {
    showError('sm-start-err', err instanceof ApiError ? err.message : 'Suche fehlgeschlagen.');
  }
}

async function handleNegotiate(key: string): Promise<void> {
  const [offerId, contactId] = key.split('::');
  if (!offerId || !contactId) return;
  try {
    const result = await api.post<{ success: boolean }>('/schwarzmarkt/negotiate', { offerId, contactId });
    showToast(result.success ? 'Verhandlung erfolgreich.' : 'Kontakt ist gegangen.', result.success ? 'info' : 'error');
    await reload();
  } catch (err) {
    showError(`sm-err-${offerId}`, err instanceof ApiError ? err.message : 'Verhandlung fehlgeschlagen.');
  }
}

async function handleFinalize(offerId: string): Promise<void> {
  const contactIds = selectedContactsByOffer[offerId] ?? [];
  try {
    const result = await api.post<FinalizeResult>('/schwarzmarkt/finalize', { offerId, contactIds });
    selectedContactsByOffer[offerId] = [];
    await reload();
    if (BigInt(result.totalPaidCents) > 0n) void refreshBalance();
    showFinalizeResultModal(result);
  } catch (err) {
    showError(`sm-err-${offerId}`, err instanceof ApiError ? err.message : 'Verkauf fehlgeschlagen.');
  }
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

export function mountSchwarzmarktTab(): void {
  if (isMounted) return;
  isMounted = true;
  attachListeners();
  void reload().then(() => { startCountdownTimer(); });
}

export async function refreshSchwarzmarktTab(): Promise<void> {
  if (!isMounted) return;
  await reload();
  startCountdownTimer();
}

export function cleanupSchwarzmarktTab(): void {
  if (countdownTimer) { clearInterval(countdownTimer); countdownTimer = null; }
  detachListeners();
  document.getElementById('sm-result-modal')?.remove();
  statusData = null;
  sellableItems = [];
  selectedSellValue = '';
  sellQty = 1;
  selectedContactsByOffer = {};
  legendExpanded = false;
  isMounted = false;
}
